import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Star, Loader2, MessageSquare, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export const Reviews = ({ productId, onRatingChange }) => {
  const { user, openAuth } = useAuth();
  const { t } = useLang();
  const [reviews, setReviews] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/products/${productId}/reviews`).then((r) => setReviews(r.data)).catch(() => setReviews([]));
  useEffect(() => { load(); }, [productId]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { openAuth("login"); return; }
    if (rating < 1) { toast.error(t("select_rating")); return; }
    setSaving(true);
    try {
      await api.post(`/products/${productId}/reviews`, { rating, comment });
      toast.success(t("review_thanks"));
      setComment(""); setRating(0);
      await load();
      onRatingChange && onRatingChange();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="mt-16" data-testid="reviews-section">
      <h2 className="font-display font-extrabold text-2xl tracking-tight mb-6 flex items-center gap-2"><MessageSquare className="w-6 h-6 text-emerald-800" /> {t("reviews")} {reviews ? `(${reviews.length})` : ""}</h2>

      <form onSubmit={submit} className="bg-white border border-stone-200 rounded-xl p-5 mb-6 max-w-2xl">
        <p className="text-sm font-medium text-stone-700 mb-2">{t("your_rating")}</p>
        <div className="flex items-center gap-1 mb-3" data-testid="rating-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)} data-testid={`star-${n}`}>
              <Star className={`w-7 h-7 transition-colors ${(hover || rating) >= n ? "fill-mango-600 text-mango-600" : "text-stone-300"}`} />
            </button>
          ))}
        </div>
        <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("review_ph")} data-testid="review-comment" />
        <Button type="submit" disabled={saving} className="mt-3 rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid="submit-review">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{t("submit_review")}
        </Button>
        {!user && <p className="text-xs text-stone-400 mt-2">{t("login_to_review")}</p>}
      </form>

      {reviews === null ? (
        <Loader2 className="w-6 h-6 animate-spin text-emerald-800" />
      ) : reviews.length === 0 ? (
        <p className="text-stone-400 text-sm" data-testid="no-reviews">{t("no_reviews")}</p>
      ) : (
        <div className="space-y-4 max-w-2xl">
          {reviews.map((r) => (
            <div key={r.id} className="bg-white border border-stone-200 rounded-xl p-4" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="w-8 h-8 rounded-full bg-emerald-800 text-white grid place-items-center text-xs font-semibold">{r.user_name?.[0]?.toUpperCase()}</span>
                  <span className="font-medium text-sm">{r.user_name}</span>
                  {r.verified && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid={`verified-badge-${r.id}`}>
                      <BadgeCheck className="w-3.5 h-3.5" /> {t("verified_purchase")}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-3.5 h-3.5 ${r.rating >= n ? "fill-mango-600 text-mango-600" : "text-stone-200"}`} />)}
                </div>
              </div>
              {r.comment && <p className="text-sm text-stone-600 mt-2 leading-relaxed">{r.comment}</p>}
              <p className="text-[11px] text-stone-400 mt-2">{new Date(r.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
